import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, RefreshCw, Play, Search, ShieldAlert, BarChart3 } from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import ModuleHeader from "@/components/ModuleHeader";

function fmtNum(n: number | null | undefined) {
  if (n == null) return "—";
  return n.toLocaleString("pt-BR");
}
function fmtDate(d: string | null | undefined) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}
function fmtBRL(v: number | null | undefined) {
  if (v == null) return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}
function cleanDoc(v: string) {
  return v.replace(/\D/g, "");
}

function StatusCard({ label, value, sub, icon }: { label: string; value: React.ReactNode; sub?: string; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <div className="text-2xl font-bold">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function SyncBadge({ syncState }: { syncState: any }) {
  if (!syncState) return null;
  if (syncState.running) return <Badge variant="secondary" className="animate-pulse">Importando…</Badge>;
  if (syncState.lastError) return <Badge variant="destructive">Erro</Badge>;
  return <Badge variant="outline">Ocioso</Badge>;
}

function AutuacoesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [cnpjInput, setCnpjInput] = useState("");
  const [cnpjBusca, setCnpjBusca] = useState("");

  const statusQ = useQuery({
    queryKey: ["/api/ibama/autuacoes/status"],
    queryFn: () => authFetch("/api/ibama/autuacoes/status"),
    refetchInterval: 5000,
  });

  const cnpjQ = useQuery({
    queryKey: ["/api/ibama/autuacoes", cnpjBusca],
    queryFn: () => authFetch(`/api/ibama/autuacoes/${cnpjBusca}`),
    enabled: !!cnpjBusca,
  });

  const importMut = useMutation({
    mutationFn: () => authFetch("/api/ibama/autuacoes/importar", { method: "POST" }),
    onSuccess: (d: any) => {
      toast({ title: "Importação iniciada", description: d.message });
      qc.invalidateQueries({ queryKey: ["/api/ibama/autuacoes/status"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const status = statusQ.data as any;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatusCard
          label="Total de Autuações"
          value={statusQ.isLoading ? <Skeleton className="h-7 w-20" /> : fmtNum(status?.total)}
          sub={`Última importação: ${fmtDate(status?.ultima_importacao)}`}
          icon={<AlertTriangle className="h-4 w-4 text-orange-600" />}
        />
        <StatusCard
          label="Status"
          value={<SyncBadge syncState={status?.syncState} />}
          sub={status?.syncState?.lastLog || "—"}
          icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Controles</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => qc.invalidateQueries({ queryKey: ["/api/ibama/autuacoes/status"] })}
              data-testid="button-refresh-ibama-aut"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Importa dados de autuações do IBAMA (dadosabertos.ibama.gov.br). Cron: diário às 00:30.
            </p>
            <Button
              onClick={() => importMut.mutate()}
              disabled={importMut.isPending || status?.syncState?.running}
              data-testid="button-importar-ibama-aut"
            >
              <Play className="h-4 w-4 mr-1" />
              {status?.syncState?.running ? "Importando…" : "Importar Autuações Agora"}
            </Button>
          </div>
          {status?.syncState?.lastError && (
            <p className="text-sm text-destructive">Último erro: {status.syncState.lastError}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" /> Consultar por CPF/CNPJ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-xs mb-1">CPF ou CNPJ (somente números)</Label>
              <Input
                placeholder="Ex: 12345678000190"
                value={cnpjInput}
                onChange={(e) => setCnpjInput(e.target.value)}
                data-testid="input-cnpj-ibama-aut"
              />
            </div>
            <Button
              className="self-end"
              onClick={() => setCnpjBusca(cleanDoc(cnpjInput))}
              disabled={!cnpjInput.trim()}
              data-testid="button-buscar-ibama-aut"
            >
              <Search className="h-4 w-4 mr-1" /> Buscar
            </Button>
          </div>

          {cnpjQ.isLoading && <Skeleton className="h-32 w-full" />}
          {cnpjQ.data && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {(cnpjQ.data as any).total} autuações encontradas para <strong>{cnpjBusca}</strong>
              </p>
              {(cnpjQ.data as any).rows?.length > 0 && (
                <div className="overflow-x-auto rounded border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº Auto</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Gravidade</TableHead>
                        <TableHead className="text-right">Multa</TableHead>
                        <TableHead>Situação</TableHead>
                        <TableHead>Município/UF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(cnpjQ.data as any).rows.slice(0, 50).map((r: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{r.num_auto_infracao || "—"}</TableCell>
                          <TableCell className="text-xs">{r.data_infracao || "—"}</TableCell>
                          <TableCell>
                            <Badge variant={["GRAVÍSSIMA","GRAVE"].includes((r.gravidade||"").toUpperCase()) ? "destructive" : "secondary"} className="text-xs">
                              {r.gravidade || "—"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">{fmtBRL(r.valor_multa)}</TableCell>
                          <TableCell className="text-xs">{r.situacao || "—"}</TableCell>
                          <TableCell className="text-xs">{r.municipio && r.uf ? `${r.municipio}/${r.uf}` : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EmbargosTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [cnpjInput, setCnpjInput] = useState("");
  const [cnpjBusca, setCnpjBusca] = useState("");

  const statusQ = useQuery({
    queryKey: ["/api/ibama/embargos/status"],
    queryFn: () => authFetch("/api/ibama/embargos/status"),
    refetchInterval: 5000,
  });

  const cnpjQ = useQuery({
    queryKey: ["/api/ibama/embargos", cnpjBusca],
    queryFn: () => authFetch(`/api/ibama/embargos/${cnpjBusca}`),
    enabled: !!cnpjBusca,
  });

  const importMut = useMutation({
    mutationFn: () => authFetch("/api/ibama/embargos/importar", { method: "POST" }),
    onSuccess: (d: any) => {
      toast({ title: "Importação iniciada", description: d.message });
      qc.invalidateQueries({ queryKey: ["/api/ibama/embargos/status"] });
    },
    onError: (e: any) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const status = statusQ.data as any;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatusCard
          label="Total de Embargos"
          value={statusQ.isLoading ? <Skeleton className="h-7 w-20" /> : fmtNum(status?.total)}
          sub={`Última importação: ${fmtDate(status?.ultima_importacao)}`}
          icon={<ShieldAlert className="h-4 w-4 text-red-600" />}
        />
        <StatusCard
          label="Status"
          value={<SyncBadge syncState={status?.syncState} />}
          sub={status?.syncState?.lastLog || "—"}
          icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Controles</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => qc.invalidateQueries({ queryKey: ["/api/ibama/embargos/status"] })}
              data-testid="button-refresh-ibama-emb"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Importa dados de embargos do IBAMA (dadosabertos.ibama.gov.br). Cron: domingos às 01:00.
            </p>
            <Button
              onClick={() => importMut.mutate()}
              disabled={importMut.isPending || status?.syncState?.running}
              data-testid="button-importar-ibama-emb"
            >
              <Play className="h-4 w-4 mr-1" />
              {status?.syncState?.running ? "Importando…" : "Importar Embargos Agora"}
            </Button>
          </div>
          {status?.syncState?.lastError && (
            <p className="text-sm text-destructive">Último erro: {status.syncState.lastError}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="h-4 w-4" /> Consultar por CPF/CNPJ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-xs mb-1">CPF ou CNPJ (somente números)</Label>
              <Input
                placeholder="Ex: 12345678000190"
                value={cnpjInput}
                onChange={(e) => setCnpjInput(e.target.value)}
                data-testid="input-cnpj-ibama-emb"
              />
            </div>
            <Button
              className="self-end"
              onClick={() => setCnpjBusca(cleanDoc(cnpjInput))}
              disabled={!cnpjInput.trim()}
              data-testid="button-buscar-ibama-emb"
            >
              <Search className="h-4 w-4 mr-1" /> Buscar
            </Button>
          </div>

          {cnpjQ.isLoading && <Skeleton className="h-32 w-full" />}
          {cnpjQ.data && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {(cnpjQ.data as any).total} embargos encontrados para <strong>{cnpjBusca}</strong>
              </p>
              {(cnpjQ.data as any).rows?.length > 0 && (
                <div className="overflow-x-auto rounded border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº Termo</TableHead>
                        <TableHead>Início</TableHead>
                        <TableHead>Situação</TableHead>
                        <TableHead className="text-right">Área (ha)</TableHead>
                        <TableHead>Município/UF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(cnpjQ.data as any).rows.map((r: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono text-xs">{r.num_termo || "—"}</TableCell>
                          <TableCell className="text-xs">{r.data_embargo_inicio || "—"}</TableCell>
                          <TableCell>
                            <Badge variant={r.situacao?.toLowerCase().includes("cancel") ? "outline" : "destructive"} className="text-xs">
                              {r.situacao || "—"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">{r.area_ha ? Number(r.area_ha).toLocaleString("pt-BR", { maximumFractionDigits: 0 }) : "—"}</TableCell>
                          <TableCell className="text-xs">{r.municipio && r.uf ? `${r.municipio}/${r.uf}` : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function IbamaPage() {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["/api/ibama/autuacoes/status"],
    queryFn: () => authFetch("/api/ibama/autuacoes/status"),
    refetchInterval: 10000,
  });
  const status = statusQ.data as any;

  return (
    <div className="space-y-6">
      <ModuleHeader
        icon={AlertTriangle}
        iconColor="text-orange-600"
        title="IBAMA — Autuações e Embargos"
        description="Infrações ambientais e embargos do Instituto Brasileiro do Meio Ambiente e dos Recursos Naturais Renováveis"
        recordCount={status?.total}
        recordLabel="autuações"
        lastUpdate={status?.ultima_importacao}
        onRefresh={() => qc.invalidateQueries({ queryKey: ["/api/ibama/autuacoes/status"] })}
        refreshing={statusQ.isFetching}
        isLoading={statusQ.isLoading}
        docsHref="/docs"
      />

      <Tabs defaultValue="autuacoes">
        <TabsList>
          <TabsTrigger value="autuacoes" data-testid="tab-ibama-autuacoes">
            <AlertTriangle className="h-4 w-4 mr-1.5" /> Autuações
          </TabsTrigger>
          <TabsTrigger value="embargos" data-testid="tab-ibama-embargos">
            <ShieldAlert className="h-4 w-4 mr-1.5" /> Embargos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="autuacoes" className="mt-6">
          <AutuacoesTab />
        </TabsContent>

        <TabsContent value="embargos" className="mt-6">
          <EmbargosTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
